// @section 1 Mission-briefing drawer — global state.
// One drawer instance lives in <AppShell>; any component (BD queue row, account
// card, "next move" cta) opens it by calling useDrawer().openBriefing(briefing).

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type BriefingMetaItem = { label: string; value: string };

export type BriefingEvidence = string;

export type BriefingActionKind = 'primary' | 'secondary';

export type BriefingAction = {
  kind: BriefingActionKind;
  label: string;
  onClick?: () => void;
};

export type BriefingStage = 'verify' | 'scope' | 'define' | 'send';

export type Briefing = {
  id: string;
  stage: BriefingStage;
  urgency?: string;
  title: string;
  meta: BriefingMetaItem[];
  situation?: string;
  whatChanged?: string;
  whyItMatters?: string;
  recommendedMove?: { move: string; reason: string };
  evidence?: BriefingEvidence[];
  actions?: BriefingAction[];
};

type Ctx = {
  open: boolean;
  briefing: Briefing | null;
  openBriefing: (b: Briefing) => void;
  close: () => void;
};

const DrawerCtx = createContext<Ctx | null>(null);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const openBriefing = useCallback((b: Briefing) => setBriefing(b), []);
  const close = useCallback(() => setBriefing(null), []);
  const value = useMemo<Ctx>(() => ({ open: !!briefing, briefing, openBriefing, close }), [briefing, openBriefing, close]);
  return <DrawerCtx.Provider value={value}>{children}</DrawerCtx.Provider>;
}

export function useDrawer(): Ctx {
  const ctx = useContext(DrawerCtx);
  if (!ctx) throw new Error('useDrawer must be used inside <DrawerProvider>');
  return ctx;
}
